import { Box } from '@mui/material'

/**
 * Google ログインボタン。
 *
 * もとは react-google-button@0.8.0 を使っていたが、以下の理由で自前化した。
 * 見た目 (240x50 / #4285f4 / 左に白 48px の G アイコン) は当時のまま。
 *
 * - `module` が指す `es/index.js` にも CommonJS を吐くパッケージングのバグがあり、
 *   vite 8 (rolldown) の `__toESM(exports, 1)` で default import が壊れて
 *   ログインページごと落ちた (rules/web.md 参照)
 * - 2024-05 以降更新が無く、次に bundler が変わればまた踏む
 * - 実体が `<div role="button">` で onKeyDown が無く、Enter / Space で押せなかった。
 *   ここでは素の `<button>` にしてキーボード操作を効かせている
 */

type GoogleSignInButtonProps = {
  onClick: () => void
  disabled?: boolean
  label?: string
}

// react-google-button の darkStyle / iconStyle をそのまま写したもの。値は触らない
const BACKGROUND_COLOR = '#4285f4'
const ICON_SIZE = 48

export const GoogleSignInButton = ({ onClick, disabled = false, label = 'Sign in with Google' }: GoogleSignInButtonProps) => {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        width: 240,
        height: 50,
        p: 0,
        border: 'none',
        borderRadius: '1px',
        boxShadow: '0 2px 4px 0 rgba(0,0,0,.25)',
        backgroundColor: BACKGROUND_COLOR,
        color: '#fff',
        fontFamily: 'Roboto,arial,sans-serif',
        fontSize: '16px',
        textAlign: 'center',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'background-color .218s, border-color .218s, box-shadow .218s',
        '&:hover': {
          boxShadow: '0 0 3px 3px rgba(66,133,244,.3)',
        },
        '&:focus-visible': {
          outline: 'none',
          boxShadow: '0 0 3px 3px rgba(66,133,244,.3)',
        },
        '&:disabled': {
          backgroundColor: 'rgba(37, 5, 5, .08)',
          color: 'rgba(0, 0, 0, .40)',
          boxShadow: 'none',
          cursor: 'not-allowed',
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: ICON_SIZE,
          height: ICON_SIZE,
          mt: '1px',
          ml: '1px',
          borderRadius: '1px',
          backgroundColor: '#fff',
          // 無効時はアイコンの白地を消して、ボタン全体のグレーに馴染ませる
          ...(disabled && { backgroundColor: 'transparent' }),
        }}
      >
        <GoogleIcon />
      </Box>
      <Box component="span" sx={{ flex: 1, lineHeight: `${ICON_SIZE}px` }}>
        {label}
      </Box>
    </Box>
  )
}

/**
 * Google の 4 色 G (logo_googleg_48dp)。
 * viewBox 46 に対して 18x18 を (15, 15) に置くのが元の配置なので、そこも合わせている。
 */
const GoogleIcon = () => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 46 46" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Google">
    <g transform="translate(15, 15)" fillRule="evenodd">
      <path
        d="M17.64,9.20454545 C17.64,8.56636364 17.5827273,7.95272727 17.4763636,7.36363636 L9,7.36363636 L9,10.845 L13.8436364,10.845 C13.635,11.97 13.0009091,12.9231818 12.0477273,13.5613636 L12.0477273,15.8195455 L14.9563636,15.8195455 C16.6581818,14.2527273 17.64,11.9454545 17.64,9.20454545 L17.64,9.20454545 Z"
        fill="#4285F4"
      />
      <path
        d="M9,18 C11.43,18 13.4672727,17.1940909 14.9563636,15.8195455 L12.0477273,13.5613636 C11.2418182,14.1013636 10.2109091,14.4204545 9,14.4204545 C6.65590909,14.4204545 4.67181818,12.8372727 3.96409091,10.71 L0.957272727,10.71 L0.957272727,13.0418182 C2.43818182,15.9831818 5.48181818,18 9,18 L9,18 Z"
        fill="#34A853"
      />
      <path
        d="M3.96409091,10.71 C3.78409091,10.17 3.68181818,9.59318182 3.68181818,9 C3.68181818,8.40681818 3.78409091,7.83 3.96409091,7.29 L3.96409091,4.95818182 L0.957272727,4.95818182 C0.347727273,6.17318182 0,7.54772727 0,9 C0,10.4522727 0.347727273,11.8268182 0.957272727,13.0418182 L3.96409091,10.71 L3.96409091,10.71 Z"
        fill="#FBBC05"
      />
      <path
        d="M9,3.57954545 C10.3213636,3.57954545 11.5077273,4.03363636 12.4404545,4.92545455 L15.0218182,2.34409091 C13.4631818,0.891818182 11.4259091,0 9,0 C5.48181818,0 2.43818182,2.01681818 0.957272727,4.95818182 L3.96409091,7.29 C4.67181818,5.16272727 6.65590909,3.57954545 9,3.57954545 L9,3.57954545 Z"
        fill="#EA4335"
      />
    </g>
  </svg>
)
